import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface SuggestionView {
  id: string; companyId: string; companyName: string; companyCategory: string;
  contactId: string; contactName: string; contactFirmName: string | null;
  referrerKind: "coi" | "advocate"; direction: "in" | "out";
  subject: string | null; occurredAt: string | null; status: "pending" | "accepted" | "dismissed";
}

/** Pending intro suggestions for a company, a COI, or everywhere, with one-click confirm / dismiss. */
export function useSuggestions(filter: { companyId?: string; contactId?: string }, opts: { list?: boolean } = {}) {
  const qc = useQueryClient();
  const params = new URLSearchParams({ status: "pending" });
  if (filter.companyId) params.set("companyId", filter.companyId);
  if (filter.contactId) params.set("contactId", filter.contactId);
  const query = useQuery<SuggestionView[]>({
    queryKey: ["suggestions", filter.companyId ?? null, filter.contactId ?? null],
    queryFn: async () => {
      const r = await fetch(`/api/origination/suggestions?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load suggestions");
      return r.json();
    },
    // The COI page already gets its pending suggestions inside /api/cois/:id and only needs accept/dismiss.
    enabled: opts.list !== false,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["suggestions"] });
    qc.invalidateQueries({ queryKey: ["origination"] });
    qc.invalidateQueries({ queryKey: ["cois"] });
    qc.invalidateQueries({ queryKey: ["coi"] });
    qc.invalidateQueries({ queryKey: ["coi-bd"] });
    qc.invalidateQueries({ queryKey: ["/api/prospects"] });
  };
  const call = (verb: "accept" | "dismiss") => async (id: string) => {
    const r = await fetch(`/api/origination/suggestions/${id}/${verb}`, { method: "POST", credentials: "include" });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `${verb} failed`);
  };
  const accept = useMutation({ mutationFn: call("accept"), onSuccess: invalidate });
  const dismiss = useMutation({ mutationFn: call("dismiss"), onSuccess: invalidate });
  return { ...query, accept, dismiss };
}
